export type RefusableCartItem = {
  key: string;
  buyableItem: {
    ticketType?: { name: string } | null;
    badgeType?: { name: string } | null;
  };
};

export type CartRefusal = {
  // Key of the cart line the reason belongs to
  key: string;
  // Displayed name of the refused ticket or badge
  name: string;
  // Reason returned by the server for that line
  reason: string;
};

/**
 * Flattens the per-line basket errors into reasons carrying the name of the
 * ticket they refuse, so a screen that does not render the cart lines (the
 * checkout) can still show the visitor which article is blocking and why.
 */
export const getCartRefusals = (
  items: RefusableCartItem[],
  getErrors: (key: string) => string[],
): CartRefusal[] =>
  items.flatMap((item) => {
    const name = getRefusedItemName(item.buyableItem);

    return getErrors(item.key).map((reason) => ({ key: item.key, name, reason }));
  });

const getRefusedItemName = (buyableItem: RefusableCartItem["buyableItem"]): string =>
  buyableItem.ticketType?.name ?? buyableItem.badgeType?.name ?? "";

// Refused line as the order endpoint returns it inside its conflict response
export type RefusedOrderLine = {
  id: string;
  error: string | null;
  buyableItem: RefusableCartItem["buyableItem"];
};

/**
 * Same reasons, read from the lines the order endpoint refused. A tunnel that submits
 * the order without ever reading a basket — the embedded ticketing — has no cart lines
 * to fill from, so its refusals come from that response instead.
 */
export const getOrderRefusals = (refusedLines: RefusedOrderLine[] = []): CartRefusal[] => {
  // The order holds one line per unit bought, so the same ticket carries the same
  // reason as many times as it was taken: the visitor only needs to read it once.
  const alreadyShown = new Set<string>();

  return refusedLines.flatMap((line) => {
    const name = getRefusedItemName(line.buyableItem);
    const refusal = `${name}|${line.error}`;

    if (!line.error || alreadyShown.has(refusal)) {
      return [];
    }

    alreadyShown.add(refusal);

    return [{ key: line.id, name, reason: line.error }];
  });
};
